import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import type { QuickLoginRow } from "@observe-tech/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { Colours, Icon } from "@/shared/coralreef";
import { AccountIdentity } from "./AccountIdentity";
import { DifferentEmailLink, ForgetEmailLink } from "./SavedAccountLinks";
import { LinkRow, Screen, ScreenBody, ScreenTitle } from "./Screen";

export interface SavedAccountsProps {
  rows: QuickLoginRow[];
  online: boolean;
  onSelect(row: QuickLoginRow): void;
  onForget(row: QuickLoginRow): void;
  onDifferentEmail(): void;
}

export const SavedAccounts: React.FC<SavedAccountsProps> = ({
  rows,
  online,
  onSelect,
  onForget,
  onDifferentEmail,
}) => {
  const { t } = useTranslation();
  const single = rows.length === 1;
  const forgetLink = (row: QuickLoginRow) => (
    <ForgetEmailLink email={row.email} onForget={() => onForget(row)} />
  );
  return (
    <Screen testId="login-saved-accounts" wide offline={!online}>
      <ScreenTitle>{t("Welcome back")}</ScreenTitle>
      <ScreenBody>{t("Choose an account to continue.")}</ScreenBody>
      {rows.map((row) => {
        const disabled = !online && !row.hasPin;
        return (
          <div key={row.cognitoSub} className="flex flex-col gap-1">
            <button
              type="button"
              className="box-border flex w-full cursor-pointer items-center gap-3 p-3 text-left enabled:hover:border-observe-primary disabled:cursor-default disabled:opacity-50 rounded-[5px] border border-solid border-observe bg-observe-dark"
              data-testid="saved-account"
              disabled={disabled}
              onClick={() => onSelect(row)}
            >
              <AccountIdentity
                email={row.email}
                detail={row.hasPin ? t("4-digit PIN") : t("Password")}
              />
              <Icon icon={faChevronRight} color={Colours.textDark} />
            </button>
            {!single && (
              <div className="flex justify-end">{forgetLink(row)}</div>
            )}
          </div>
        );
      })}
      <LinkRow>
        <DifferentEmailLink online={online} onClick={onDifferentEmail} />
        {single && forgetLink(rows[0])}
      </LinkRow>
    </Screen>
  );
};
